import { createIyzicoCheckoutSessionFn } from "@/actions/payment/handler";
import { formatDaysPerWeek } from "@/api/db/schema/training-programs";
import { authClient } from "@/auth/auth-client";
import { IyzicoCheckoutDialog } from "@/components/payment/IyzicoCheckoutDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Calendar,
  CheckCircle2,
  Clock,
  Lock,
  MessageCircle,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";

interface PurchasePreviewDialogProps {
  programId?: string;
  catalogItemId?: string;
  programName: string;
  priceKurus?: number;
  durationWeeks?: number;
  daysPerWeek?: number;
  maxDaysPerWeek?: number | null;
  children: ReactNode;
}

export function PurchasePreviewDialog({
  programId,
  programName,
  priceKurus,
  durationWeeks,
  daysPerWeek,
  maxDaysPerWeek,
  children,
}: PurchasePreviewDialogProps) {
  const navigate = useNavigate();
  const { data: session } = authClient.useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [fallbackToSupport, setFallbackToSupport] = useState(false);

  const priceFormatted = priceKurus
    ? `${(priceKurus / 100).toLocaleString("tr-TR")} TL`
    : "Fiyat için iletişime geçin";

  const checkoutMutation = useMutation({
    mutationFn: createIyzicoCheckoutSessionFn,
    onSuccess: (res) => {
      if (
        res.status === 200 &&
        res.body &&
        "paymentPageUrl" in res.body &&
        res.body.paymentPageUrl
      ) {
        setCheckoutUrl(res.body.paymentPageUrl);
        setIsOpen(false);
        setIsCheckoutOpen(true);
      } else {
        toast.error(
          "Online ödeme tamamlanamadı. WhatsApp veya Havale/EFT ile devam edebilirsiniz.",
        );
        setFallbackToSupport(true);
      }
    },
    onError: (_err) => {
      toast.error(
        "Online ödeme şu anda tamamlanamıyor. WhatsApp veya Havale/EFT ile devam edebilirsiniz.",
      );
      setFallbackToSupport(true);
    },
  });

  const handleStartPurchase = () => {
    if (!session?.user) {
      toast.info("Programı satın almak için lütfen giriş yapın.");
      void navigate({
        to: "/password/login",
        search: { redirect: "/program" },
      });
      return;
    }

    if (!programId) {
      setFallbackToSupport(true);
      toast.info("Satın alma işlemini WhatsApp üzerinden tamamlayabilirsiniz.");
      return;
    }

    checkoutMutation.mutate({
      data: {
        programId,
      },
    });
  };

  const whatsappMessage = encodeURIComponent(
    `Merhaba, "${programName}" programını satın almak ve kaydımı tamamlamak istiyorum.`,
  );
  const whatsappUrl = `https://wa.me/905449642824?text=${whatsappMessage}`;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent className="max-h-[95vh] w-[95vw] max-w-lg overflow-y-auto rounded-2xl border bg-background p-6 shadow-2xl sm:w-full">
          <DialogHeader className="text-left">
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="border-primary/20 bg-primary/10 text-primary"
              >
                <Sparkles className="mr-1 h-3 w-3" /> Dijital Program
              </Badge>
              {durationWeeks && (
                <span className="flex items-center gap-1 font-semibold text-muted-foreground text-xs">
                  <Calendar className="h-3.5 w-3.5" /> {durationWeeks} Hafta
                </span>
              )}
              {daysPerWeek && (
                <span className="flex items-center gap-1 font-semibold text-muted-foreground text-xs">
                  <Clock className="h-3.5 w-3.5" />{" "}
                  {formatDaysPerWeek(daysPerWeek, maxDaysPerWeek)}
                </span>
              )}
            </div>
            <DialogTitle className="mt-2 font-black text-2xl text-foreground tracking-tight">
              {programName}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Satın alma öncesi ürün ve teslimat detayları
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Price banner */}
            <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5">
              <p className="font-semibold text-muted-foreground text-xs uppercase tracking-wider">
                Tek Seferlik Ödeme (KDV Dâhil)
              </p>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-black text-4xl text-foreground tracking-tight">
                  {priceFormatted}
                </span>
                <span className="text-muted-foreground text-xs">
                  / ömür boyu erişim
                </span>
              </div>
            </div>

            {/* Feature list */}
            <ul className="space-y-3.5 text-sm">
              <li className="flex items-start gap-3">
                <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <strong className="text-foreground">
                    Anında Dijital Erişim:
                  </strong>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    Ödeme tamamlandığı an program içeriği ve antrenman günlüğü
                    hesabınıza tanımlanır.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div>
                  <strong className="text-foreground">
                    İyzico Güvenli Ödeme:
                  </strong>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    256-bit SSL ve 3D Secure ile korunur. Kredi kartı
                    bilgileriniz sistemde asla saklanmaz.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Lock className="h-4 w-4" />
                </div>
                <div>
                  <strong className="text-foreground">Yasal Güvence:</strong>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    Mesafeli Satış Sözleşmesi ve Ön Bilgilendirme Formu
                    onayınızla güvenli satın alma.
                  </p>
                </div>
              </li>
            </ul>

            {fallbackToSupport && (
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-xs">
                <p className="font-semibold text-amber-800 dark:text-amber-300">
                  Online ödeme şu anda tamamlanamıyor.
                </p>
                <p className="mt-1 text-muted-foreground">
                  Havale/EFT ödeme ve aktivasyon süreci için doğrudan WhatsApp
                  üzerinden yazabilirsiniz.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-col">
            {fallbackToSupport && (
              <Button
                asChild
                className="h-auto min-h-12 w-full whitespace-normal rounded-xl bg-emerald-600 py-3 font-bold text-white hover:bg-emerald-700"
              >
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-4 w-4 shrink-0" />
                  WhatsApp ile Satın Al / Havale-EFT ile Tamamla
                </a>
              </Button>
            )}
            {!session?.user ? (
              <Button
                variant={fallbackToSupport ? "outline" : "default"}
                onClick={handleStartPurchase}
                className="h-12 w-full rounded-xl font-bold shadow-lg shadow-primary/10"
              >
                Giriş Yaparak Satın Al
              </Button>
            ) : (
              <Button
                variant={fallbackToSupport ? "outline" : "default"}
                onClick={handleStartPurchase}
                disabled={checkoutMutation.isPending}
                className="h-12 w-full rounded-xl font-bold shadow-lg shadow-primary/10"
              >
                {checkoutMutation.isPending
                  ? "Ödeme Formu Hazırlanıyor..."
                  : "Güvenli Ödemeye Geç"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embedded Iyzico Checkout Modal when URL is returned */}
      <IyzicoCheckoutDialog
        open={isCheckoutOpen}
        onOpenChange={setIsCheckoutOpen}
        paymentPageUrl={checkoutUrl}
        title={`${programName} Satın Al`}
      />
    </>
  );
}
